/**
 * IncomingCallOverlay — full-screen ringing UI for incoming audio/video calls.
 * Sprint B scaffolding : static layout ready to be wired to a real LiveKit
 * session + Socket.io signaling. For now, accepts `call` prop and two
 * callbacks (onAccept / onDecline) — parent controls whether to render.
 *
 * Mobile-first :
 *   - Full viewport overlay with safe-area insets (via calls.css)
 *   - Two large 56px buttons → touch-friendly
 *   - No horizontal scroll
 *   - Works on 320px and up
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, PhoneSlash, VideoCamera } from '@phosphor-icons/react';
import './calls.css';
import { CallControl } from './CallControls';

export default function IncomingCallOverlay({ call, onAccept, onDecline }) {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n?.language || '').startsWith('ar');
  useEffect(() => {
    // Escape = decline
    const onEsc = (e) => e.key === 'Escape' && onDecline?.();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onDecline]);

  if (!call) return null;
  const {
    caller_name = '',
    caller_avatar = '',
    mode = 'audio',
  } = call;
  const initial = (caller_name?.[0] || '?').toUpperCase();
  const isVideo = mode === 'video';

  return (
    <div
      className="jp-call-overlay jp-call-light"
      data-testid="incoming-call-overlay"
      role="dialog"
      aria-modal="true"
      dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="jp-call-header">
        <span>{isVideo ? t('calls.incoming_video_call') : t('calls.incoming_audio_call')}</span>
        <span />
      </div>
      <div className="jp-call-body">
        <div className="jp-call-avatar jp-ringing" aria-hidden="true">
          {caller_avatar ? (
            <img src={caller_avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          ) : initial}
        </div>
        <p className="jp-call-name">{caller_name}</p>
        <p className="jp-call-status">{isVideo ? t('calls.wants_to_video_call') : t('calls.wants_to_talk')}</p>
      </div>
      <div className="jp-call-controls">
        <div className="jp-call-controls-row">
          <CallControl onClick={onDecline} label={t('calls.decline')} data-testid="incoming-call-decline" className="jp-call-btn-end">
            <PhoneSlash size={28} weight="fill" />
          </CallControl>
          <CallControl onClick={onAccept} label={t('calls.accept')} data-testid="incoming-call-accept" className="jp-call-btn-accept">
            {isVideo ? <VideoCamera size={28} weight="fill" /> : <Phone size={28} weight="fill" />}
          </CallControl>
        </div>
      </div>
    </div>
  );
}
